import React from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CurrentPatientProvider } from './contexts/CurrentPatientContext';
import { ClinicProvider } from './contexts/ClinicContext';
import AppLayout from './components/AppLayout';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import UploadReferralPage from './pages/UploadReferralPage';
import PatientFormPage from './pages/PatientFormPage';
import LettersPage from './pages/LettersPage';
import UsersPage from './pages/UsersPage';
import PatientFilePage from './pages/PatientFilePage';
import ContactsPage from './pages/ContactsPage';
import ClinicsPage from './pages/ClinicsPage';
import OutboxPage from './pages/OutboxPage';
import DiaryPage from './pages/DiaryPage';
import PreferencesPage from './pages/PreferencesPage';
import ACCAssessmentUpload from './components/ACCAssessmentUpload';
import TBI14Summary from './components/TBI14Summary';
import BookingConfirmedPage from './pages/BookingConfirmedPage';
import BookingCancelledPage from './pages/BookingCancelledPage';
import ConfirmBookingPage from './pages/ConfirmBookingPage';
import CancelBookingPage from './pages/CancelBookingPage';
import ReferrerLoginPage from './pages/ReferrerLoginPage';
import ReferrerDashboard from './pages/ReferrerDashboard';
import ReferrerNewReferral from './pages/ReferrerNewReferral';
import ReferrerReferralDetail from './pages/ReferrerReferralDetail';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ImpairmentToolsPage from './pages/ImpairmentTools/ImpairmentToolsPage';
import RespiratoryCalculator from './pages/ImpairmentTools/RespiratoryCalculator';
import ReportReview from './pages/ImpairmentTools/ReportReview';
import ImpairmentAnalysis from './pages/ImpairmentTools/ImpairmentAnalysis';
import FellowshipPage from './pages/FellowshipPage';
import ScribePage from './pages/ScribePage';
import './App.css';

// SECURITY: Auto-add auth token to all axios requests
axios.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// SECURITY: Handle expired/invalid tokens globally
// Only log out on genuine auth token failures from protected endpoints
let isLoggingOut = false;
const AUTH_ENDPOINTS = ['/auth/login', '/auth/verify-mfa', '/auth/resend-mfa'];
const AUTH_ERRORS = ['Token expired', 'Invalid token', 'Authentication required'];

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isCancel(error) || !error.response) {
      return Promise.reject(error);
    }

    if (error.response.status === 401) {
      const url = error.config?.url || '';
      // Don't log out on auth endpoint 401s (wrong credentials / invalid MFA code)
      const isAuthEndpoint = AUTH_ENDPOINTS.some(ep => url.includes(ep));
      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      // Only log out if the error message indicates a genuine token problem
      const errorMsg = error.response.data?.error || '';
      const isTokenError = AUTH_ERRORS.some(msg => errorMsg.includes(msg));

      if (isTokenError && sessionStorage.getItem('token') && !isLoggingOut) {
        isLoggingOut = true;
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        // Notify AuthContext to update React state (avoids destructive page reload)
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
        // Reset guard after a short delay to allow future logouts in new sessions
        setTimeout(() => { isLoggingOut = false; }, 2000);
      }
    }

    return Promise.reject(error);
  }
);

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" />;
  }
  return children;
}

function ACCRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  if (!user.canViewACC) return <Navigate to="/dashboard" />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <ClinicProvider>
      <CurrentPatientProvider>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/fellowship" element={<FellowshipPage />} />
          <Route path="/patient/:token" element={<PatientFormPage />} />
          <Route path="/booking-confirmed" element={<BookingConfirmedPage />} />
          <Route path="/booking-error" element={<BookingConfirmedPage />} />
          <Route path="/booking-cancelled" element={<BookingCancelledPage />} />
          <Route path="/confirm-booking/:token" element={<ConfirmBookingPage />} />
          <Route path="/cancel-booking/:token" element={<CancelBookingPage />} />
          
          {/* Referrer Portal (public auth, own session) */}
          <Route path="/referrer/login" element={<ReferrerLoginPage />} />
          <Route path="/referrer/dashboard" element={<ReferrerDashboard />} />
          <Route path="/referrer/new" element={<ReferrerNewReferral />} />
          <Route path="/referrer/referral/:id" element={<ReferrerReferralDetail />} />
          
          {/* Protected Routes (staff) - wrapped in AppLayout with sidebar */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AppLayout><DashboardPage /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/upload"
            element={
              <ProtectedRoute>
                <AppLayout><UploadReferralPage /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/letters"
            element={
              <ProtectedRoute>
                <AppLayout><LettersPage /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <AppLayout><UsersPage /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patient-file/:id"
            element={
              <ProtectedRoute>
                <AppLayout><PatientFilePage /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/contacts"
            element={
              <ProtectedRoute>
                <AppLayout><ContactsPage /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/clinics"
            element={
              <ProtectedRoute>
                <AppLayout><ClinicsPage /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/outbox"
            element={
              <ProtectedRoute>
                <AppLayout><OutboxPage /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/diary"
            element={
              <ProtectedRoute>
                <AppLayout><DiaryPage /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/preferences"
            element={
              <ProtectedRoute>
                <AppLayout><PreferencesPage /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/acc-assessment"
            element={
              <ACCRoute>
                <AppLayout><ACCAssessmentUpload /></AppLayout>
              </ACCRoute>
            }
          />
          <Route
            path="/tbi14"
            element={
              <ACCRoute>
                <AppLayout><TBI14Summary /></AppLayout>
              </ACCRoute>
            }
          />
          
          <Route
            path="/scribe"
            element={
              <ProtectedRoute>
                <AppLayout><ScribePage /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/impairment"
            element={
              <ACCRoute>
                <AppLayout><ImpairmentToolsPage /></AppLayout>
              </ACCRoute>
            }
          />
          <Route
            path="/impairment/respiratory"
            element={
              <ACCRoute>
                <AppLayout><RespiratoryCalculator /></AppLayout>
              </ACCRoute>
            }
          />
          <Route
            path="/impairment/review"
            element={
              <ACCRoute>
                <AppLayout><ReportReview /></AppLayout>
              </ACCRoute>
            }
          />
          <Route
            path="/impairment/analysis/:id"
            element={
              <ACCRoute>
                <AppLayout><ImpairmentAnalysis /></AppLayout>
              </ACCRoute>
            }
          />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
      </CurrentPatientProvider>
      </ClinicProvider>
    </AuthProvider>
  );
}

export default App;
